// TypeScript interfaces for Work Order module

// Approval workflow enums and interfaces
export enum ApprovalStatus {
  Draft = "Draft",
  SubmittedForApproval = "Submitted for Approval", 
  Checked = "Checked",
  Verified = "Verified",
  Approved = "Approved",
  ReturnedForCorrection = "Returned for Correction",
  Released = "Released"
}

export enum UserRole {
  Creator = "Creator",
  Checker = "Checker", 
  Verifier = "Verifier",
  Approver = "Approver"
}

export interface ApprovalHistoryItem {
  action: string;
  actionBy: string;
  actionDate: string;
  role: UserRole;
  comments?: string;
}

export interface PendingWorkOrderData {
  id: string;
  workOrderNumber: string;
  workOrderTitle: string;
  workOrderDescription?: string;
  siteId: string;
  siteName: string;
  projectId?: string;
  projectName?: string;
  contractType: string;
  contractWith: string;
  contractorId: string;
  contractorName: string;
  workOrderOwner: string;
  plannedStartDate: string;
  plannedEndDate: string;
  totalValue: number;
  taxAmount: number;
  grossValue: number;
  status: ApprovalStatus;
  createdAt: string;
  createdBy: string;
  lastActionBy: string;
  lastActionDate: string;
  lastActionRole: UserRole;
  remarks?: string;
  approvalHistory: ApprovalHistoryItem[];
}

export interface WorkOrderData {
  id: string;
  workOrderNumber: string;
  linkedWorkRequest?: string;
  linkedTender?: string;
  contractDate: string;
  workOrderTitle: string;
  workOrderDescription?: string;
  siteId: string;
  siteName: string;
  projectId?: string;
  projectName?: string;
  contractType: ContractType;
  contractWith: ContractWith;
  contractorId: string;
  contractorName: string;
  workOrderOwner: string;
  plannedStartDate: string;
  plannedEndDate: string;
  approvalRequired: boolean;
  enableTax: boolean;
  taxApplicationLevel?: any;
  status: WorkOrderStatus;
  workOrderLines: WorkOrderLine[];
  generalConditions: GeneralCondition[];
  paymentConditions: PaymentConditions;
  totalValue: number;
  taxAmount: number;
  grossValue: number;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  approverComments?: string;
  rejectionComments?: string;
}

export interface WorkOrderLine {
  id: string;
  lineNumber: string;
  workCategory: string;
  subCategory?: string;
  taskTitle: string;
  taskDescription?: string;
  quantity: number;
  uom: string;
  weightagePercentage: number;
  rate: number;
  taxType?: string;
  taxPercentage?: number;
  taxAmount: number;
  totalAmount: number;
  source: WorkOrderLineSource;
  sourceId?: string; // PM task ID, BOQ line ID, etc.
  sectionId?: string;
  executedQuantity?: number;
  remainingQuantity?: number;
  completionPercentage?: number;
}

export interface GeneralCondition {
  id: string;
  conditionName: string;
  conditionContent: string;
  isActive: boolean;
  order: number;
  attachments?: ConditionAttachment[];
}

export interface ConditionAttachment {
  id: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  uploadDate: string;
  url?: string;
}

export interface PaymentConditions {
  mobilizationAdvance: MobilizationAdvance;
  retentionAmount: RetentionAmount;
  onHoldAmount: OnHoldAmount;
  payOnWorkCompletion: boolean;
}

export interface MobilizationAdvance {
  isPercentOfContract: boolean;
  percentageOfContract?: number;
  advanceValue?: number;
  interestCharged: boolean;
  interestRate?: number;
  recoveryType: any;
}

export interface RetentionAmount {
  isPercentOfRABill: boolean;
  percentageOfRABill?: number;
  retentionValue?: number;
  dlpAmount?: number;
  dlpUnit?: 'days' | 'months' | 'years';
  releaseDate: any;
}

export interface OnHoldAmount {
  isPercentOfContract: boolean;
  percentageOfContract?: number;
  holdAmountValue?: number;
  dlpAmount?: number;
  dlpUnit?: 'days' | 'months' | 'years';
  releaseDate: any;
}

// Project Management related interfaces
export interface ProjectTask {
  taskId: string;
  taskName: string;
  workCategory: string;
  description: string;
  quantity: number;
  uom: string;
  weightagePercentage: number;
  rate: number;
  taxType: string;
  taxPercentage: number;
  activityId: string;
  activityName: string;
  projectId: string;
  isSelected?: boolean;
}

// Work BOQ related interfaces
export interface WorkBoqLine {
  lineId: string;
  taskName: string;
  workCategory: string;
  approvedQuantity: number;
  allocatedQuantity: number;
  pendingQuantity: number;
  uom: string;
  weightagePercentage: number;
  rate: number;
  taxType: string;
  taxPercentage: number;
  workBoqId: string;
  isSelected?: boolean;
}

// Master data interfaces
export interface Site {
  id: string;
  name: string;
}

export interface Project {
  id: string;
  name: string;
  siteId: string;
}

export interface Activity {
  id: string;
  name: string;
  projectId: string;
  description?: string;
}

export interface WorkBoq {
  id: string;
  title: string;
  siteId: string;
  projectId?: string;
  status: string;
}

export interface Contractor {
  id: string;
  companyName: string;
  contactPerson: string;
  email: string;
  phone: string;
  siteIds: string[];
}

export interface WorkCategory {
  id: string;
  name: string;
  parentId?: string;
  description?: string;
}

export interface UOM {
  id: string;
  name: string;
  code: string;
}

export interface TaxType {
  id: string;
  name: string;
  percentage: number;
  code: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
}

export interface WorkRequest {
  id: string;
  workRequestNumber: string;
  workRequestTitle: string;
  siteId: string;
  status: string;
}

export interface Tender {
  id: string;
  tenderNumber: string;
  tenderTitle: string;
  siteId: string;
  status: string;
}

export interface MasterData {
  sites: Site[];
  projects: Project[];
  activities: Activity[];
  workBoqs: WorkBoq[];
  contractors: Contractor[];
  workCategories: WorkCategory[];
  uomList: UOM[];
  taxTypes: TaxType[];
  users: User[];
  workRequests: WorkRequest[];
  tenders: Tender[];
}

// Predefined general conditions
export interface PredefinedCondition {
  id: string;
  name: string;
  defaultContent: string;
  order: number;
}

// Enums
export enum ContractType {
  ConstructionWork = "Construction Work",
  Service = "Service",
  Maintenance = "Maintenance",
  Others = "Others"
}

export enum ContractWith {
  GeneralContractor = "General Contractor",
  SubContractor = "Sub Contractor", 
  Others = "Others"
}

export enum TaxApplicationLevel {
  LineLevel = "Line Level",
  WorkOrderLevel = "Work Order Level"
}

export enum WorkOrderStatus {
  Draft = "Draft",
  PendingApproval = "Pending Approval",
  Rejected = "Rejected",
  Approved = "Approved",
  Released = "Released",
  ContractorConfirmed = "Contractor Confirmed",
  InExecution = "In Execution",
  OnHold = "On Hold",
  VariationPending = "Variation Pending",
  Amended = "Amended",
  Completed = "Completed",
  Closed = "Closed",
  Cancelled = "Cancelled"
}

export enum WorkOrderLineSource {
  ProjectManagement = "Project Management",
  WorkBOQ = "Work BOQ",
  NewWorkLine = "New Work Line"
}

export enum RecoveryType {
  EachRABill = "Each RA Bill",
  FinalBill = "Final Bill"
}

// Form interfaces
export interface WorkOrderFormData {
  // General Information (Section A)
  workOrderNumber: string;
  linkedWorkRequest?: string;
  linkedTender?: string;
  contractDate: any;
  workOrderTitle: string;
  workOrderDescription?: string;
  siteId: string;
  siteName?: string;
  projectId?: string;
  projectName?: string;
  contractType: ContractType | string;
  contractWith: ContractWith | string;
  contractorId: string;
  contractorName?: string;
  workOrderOwner: string;
  plannedStartDate: any;
  plannedEndDate: any;
  approvalRequired: string; // "true" | "false" for form handling
  enableTax: string; // "true" | "false" for form handling
  taxApplicationLevel?: TaxApplicationLevel | string;
  
  // Work Order Information (Section B)
  selectedSource?: WorkOrderLineSource;
  activityId?: string; // For Project Management source
  workCategoryId?: string; // For Project Management source
  workBoqId?: string; // For Work BOQ source
  workOrderLines: WorkOrderLine[];
  
  // General Conditions (Section C)
  generalConditions: GeneralCondition[];
  
  // Payment Conditions (Section D)
  paymentConditions: PaymentConditions;
  
  // Internal fields
  status?: WorkOrderStatus;
  type?: "create" | "edit" | "duplicate";
  createdBy?: string;
  createdAt?: string;
  approvalsData?: any;
}

// Payment Conditions form interfaces
export interface PaymentConditionsFormData {
  // Mobilization Advance
  mobilizationIsPercent: string; // "true" | "false"
  mobilizationPercentage?: number;
  mobilizationValue?: number;
  mobilizationInterestCharged: string; // "true" | "false"
  mobilizationInterestRate?: number;
  mobilizationRecoveryType: RecoveryType | any;
  
  // Retention Amount
  retentionIsPercent: string; // "true" | "false"
  retentionPercentage?: number;
  retentionValue?: number;
  retentionReleaseDate: string | null;
  
  // On Hold Amount
  onHoldIsPercent: string; // "true" | "false"
  onHoldPercentage?: number;
  onHoldValue?: number;
  onHoldReleaseDate: string | null;
  
  // Pay on Work Completion
  payOnWorkCompletion: string; // "true" | "false"
}

// Filter interfaces
export interface WorkOrderFilters {
  searchText?: string;
  siteIds?: string[];
  projectIds?: string[];
  statuses?: WorkOrderStatus[];
  contractTypes?: ContractType[];
  contractorIds?: string[];
  contractWith?: ContractWith[];
  approvalRequired?: boolean;
  enableTax?: boolean;
  workOrderValueRange?: {
    min: number;
    max: number;
  };
  dateRange?: {
    startDate: string;
    endDate: string;
  };
  createdBy?: string[];
}

// API Response interfaces
export interface ApiResponse<T> {
  data: T;
  status: boolean;
  message: string;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
  };
}

// Component Props interfaces
export interface WorkOrderTableProps {
  data: WorkOrderData[];
  loading?: boolean;
  onEdit?: (record: WorkOrderData) => void;
  onView?: (record: WorkOrderData) => void;
  onDelete?: (record: WorkOrderData) => void;
  onSubmit?: (record: WorkOrderData) => void;
  onApprove?: (record: WorkOrderData) => void;
  onReject?: (record: WorkOrderData) => void;
  onRelease?: (record: WorkOrderData) => void;
  onCancel?: (record: WorkOrderData) => void;
}

export interface TaskSelectorProps {
  activityId?: string;
  workCategoryId?: string;
  workBoqId?: string;
  onLinesSelected: (lines: WorkOrderLine[]) => void;
  selectedLines?: WorkOrderLine[];
  editingLine?: WorkOrderLine;
  siteId?: string;
}

export interface GeneralConditionsProps {
  conditions: GeneralCondition[];
  onConditionsChange: (conditions: GeneralCondition[]) => void;
}

export interface PaymentConditionsProps {
  conditions: PaymentConditions;
  onConditionsChange: (conditions: PaymentConditions) => void;
}

// Step component props
export interface StepComponentProps {
  next?: (data?: any) => void;
  prev?: (data?: any) => void;
  current?: number;
  [key: string]: any;
}

// Execution related interfaces (for future implementation)
export interface ExecutionBatch {
  id: string;
  workOrderId: string;
  sectionId: string;
  executionPeriodFrom: string;
  executionPeriodTo: string;
  notes?: string;
  attachments: string[];
  lines: ExecutionLine[];
  status: ExecutionBatchStatus;
  submittedBy: string;
  submittedAt: string;
  checkerComments?: string;
  verifierComments?: string;
  approverComments?: string;
}

export interface ExecutionLine {
  workLineId: string;
  executedQuantity: number;
  remarks?: string;
}

export enum ExecutionBatchStatus {
  Draft = "Draft",
  Submitted = "Submitted",
  Checked = "Checked",
  Verified = "Verified",
  Approved = "Approved",
  Rejected = "Rejected"
}

// Section interface for execution
export interface WorkOrderSection {
  id: string;
  name: string;
  workOrderId: string;
  lines: WorkOrderLine[];
  progressPercentage: number;
  lastUpdate?: string;
}